import React, { useEffect, useState } from "react";
import { makeAutoObservable } from "mobx";
import { observer } from "mobx-react-lite";
import { ChevronDownIcon, ChevronRight, X } from "lucide-react";
import Loader from "./Loader";
import useOrder from "../hooks/useOrder";
import { toast } from "react-hot-toast";
import AppStore from "../store/index";
import PasswordDialog from "./PasswordInput";

const OTHER_PAYMENT_METHODS = [
  "zomato dining/district",
  "swiggy/dineout",
  "magicpin",
  "sodexo",
  "eazydiner",
  "in-house",
];

// Regex pattern for swiggy/dineout variations
const SWIGGY_DINEOUT_REGEX = /^[\s⁠]*swiggy[\s⁠]*(\/|\\)?[\s⁠]*dineout[\s⁠]*$/i;

// MobX Store
class OrderBillerStore {
  order = null;
  isExpanded = true;
  amountPaid = "";
  orderPaymentMethod = "Cash";
  orderOtherPaymentMethod = "";
  partialCashPayment = "";
  partialOnlinePayment = "";
  partialCardPayment = "";
  flatDiscount = 0;
  percentageDiscount = 0;
  tip = 0;
  couponCode = "";
  loading = false;
  orderWaiter = "none";

  constructor() {
    makeAutoObservable(this);
    this.order = null;
    this.flatDiscount = 0;
    this.percentageDiscount = 0;
    this.tip = 0;
    this.orderPaymentMethod = "NA";
    this.orderOtherPaymentMethod = "zomato dining/district";
    this.partialCashPayment = 0;
    this.partialOnlinePayment = 0;
    this.partialCardPayment = 0;
  }

  getBillDetails(items) {
    const { GST } = AppStore;
    const { active, fassi_no, gstNumber, gstType, rates } = GST;
    const { ACCGST, ACSGST, CGST, SGST, vat } = rates;

    const cart = items;
    const subtotal = cart
      .filter((item) => !item.canceled && item.status !== "rejected")
      .reduce((sum, item) => sum + item.price * item.quantity, 0);

    let calculated_cgst = 0;
    let calculated_sgst = 0;
    let serviceTax = 0;
    let discount = 0;
    let tip = 0;
    let serviceCharge = 0;

    let total = subtotal > 0 ? subtotal - discount : subtotal;

    if (active) {
      calculated_cgst = (total * parseFloat(CGST || 0)) / 100;
      calculated_sgst = (total * parseFloat(SGST || 0)) / 100;
      serviceTax =
        parseInt(serviceCharge || 0) > 0
          ? total * (parseFloat(serviceCharge) / 100)
          : 0;
    }

    if (gstType === "Exclude") {
      total += Math.floor(calculated_cgst + calculated_sgst);
    }

    let orderBillAmount = Math.round(total);
    orderBillAmount += tip;

    return {
      beforeDiscountTotal: subtotal,
      cgst: calculated_cgst,
      cgstPercent: CGST,
      discount: 0,
      discountFlat: 0,
      discountPercent: 0,
      gst: active,
      gstExcluded: gstType === "Exclude",
      gstNo: gstNumber,
      service: false,
      sgst: calculated_sgst,
      sgstPercent: SGST,
      subTotal: subtotal,
      tip: tip,
      total: orderBillAmount,
      paymentType: "NA",
    };
  }

  setOrder(order) {
    this.order = order;
    if (order.billTxnLogs === "NA") {
      this.order.billTxnLogs = this.getBillDetails(order.items);
    }

    this.flatDiscount = order.billTxnLogs.discountFlat || 0;
    this.percentageDiscount = order.billTxnLogs.discountPercent || 0;
    this.tip = order.tips || 0;
    this.orderPaymentMethod =
      order.paymentmethod !== "NA" ? order.paymentmethod : "NA";
    if (this.orderPaymentMethod === "partial") {
      order.partialAmoutsplit.forEach((item) => {
        if (item.name === "cash") {
          this.partialCashPayment = item.amount;
        } else if (item.name === "online") {
          this.partialOnlinePayment = item.amount;
        } else if (item.name === "card") {
          this.partialCardPayment = item.amount;
        }
      });
    } else if (OTHER_PAYMENT_METHODS.includes(order.paymentmethod)) {
      this.orderPaymentMethod = "other";
      this.orderOtherPaymentMethod = order.paymentmethod;
    } else if (SWIGGY_DINEOUT_REGEX.test(order.paymentmethod)) {
      this.orderPaymentMethod = "other";
      this.orderOtherPaymentMethod = "swiggy/dineout";
    }
  }

  setIsExpanded(value) {
    this.isExpanded = value;
  }

  setAmountPaid(value) {
    this.amountPaid = value;
  }

  setOrderPaymentMethod(method) {
    this.orderPaymentMethod = method;
  }

  setOrderOtherPaymentMethod(method) {
    this.orderOtherPaymentMethod = method;
  }

  setPartialPayment(type, value) {
    switch (type) {
      case "Cash":
        this.partialCashPayment = value;
        break;
      case "Online":
        this.partialOnlinePayment = value;
        break;
      case "Card":
        this.partialCardPayment = value;
        break;
      default:
        break;
    }
  }

  setFlatDiscount(value) {
    this.flatDiscount = value;
  }

  setPercentageDiscount(value) {
    this.percentageDiscount = value;
  }

  setTip(value) {
    this.tip = value;
  }

  setCouponCode(value) {
    this.couponCode = value;
  }

  setLoading(value) {
    this.loading = value;
  }

  setOrderWaiter(value) {
    this.orderWaiter = value;
  }

  get orderTotal() {
    if (!this.order) return 0;
    return this.order.billTxnLogs.total;
  }

  get subTotal() {
    if (!this.order) return 0;
    return this.order.billTxnLogs.subTotal;
  }

  get orderBillAmount() {
    if (!this.order) return 0;
    let total = this.orderTotal;

    // Apply flat discount
    total -= this.flatDiscount;

    // Apply percentage discount
    if (this.percentageDiscount > 0) {
      total -= total * (this.percentageDiscount / 100);
    }

    return Math.round(total);
  }

  get totalDiscount() {
    if (!this.order) return 0;
    let discount = this.flatDiscount;
    if (this.percentageDiscount > 0) {
      discount += this.orderTotal * (this.percentageDiscount / 100);
    }
    return Math.round(discount);
  }

  get orderCGST() {
    return this.order?.billTxnLogs.cgst || 0;
  }

  get orderSGST() {
    return this.order?.billTxnLogs.sgst || 0;
  }

  get orderServiceTax() {
    return this.order?.billTxnLogs.service ? this.subTotal * 0.05 : 0;
  }
}

const store = new OrderBillerStore();

// Updated components
const OrderBiller = observer(({ isOpen, onClose, order, onUpdate }) => {
  const PASSWORD = AppStore.restaurantInfo.masterPassword;
  const { completeOrderBill } = useOrder();
  const [showCancelled, setShowCancelled] = useState(false);
  const [showRejected, setShowRejected] = useState(false);
  const [accessGranted, setAccessGranted] = useState(false);
  const [showPasswordDialogue, setShowPasswordDialogue] = useState(true);

  useEffect(() => {
    if (order) {
      store.setOrder(order);
    }
  }, [order]);

  // Filter functions for items
  const activeItems =
    store.order?.items.filter(
      (item) => !item.isCancelled && item.status !== "rejected"
    ) || [];

  const cancelledItems =
    store.order?.items.filter((item) => item.isCancelled) || [];

  const rejectedItems =
    store.order?.items.filter((item) => item.status === "rejected") || [];

  const handleFlatDiscountChange = (e) => {
    const value = e.target.value;
    if (!isNaN(value) && value >= 0) {
      store.setFlatDiscount(Number(value));
    }
  };

  const handlePercentageDiscountChange = (e) => {
    const value = e.target.value;
    if (!isNaN(value) && value >= 0 && value <= 100) {
      store.setPercentageDiscount(Number(value));
    }
  };

  const handleTipChange = (e) => {
    const value = e.target.value;
    if (!isNaN(value) && value >= 0) {
      store.setTip(Number(value));
    }
  };

  const handlePartialPaymentChange = (e, type) => {
    const value = e.target.value;
    if (!isNaN(value) && value >= 0) {
      store.setPartialPayment(type, Number(value));
    }
  };

  // Add total calculation functions
  const calculateItemsTotal = (items) => {
    return items.reduce(
      (total, item) => total + Number(item.price) * item.quantity,
      0
    );
  };

  const cancelledTotal = calculateItemsTotal(cancelledItems);
  const rejectedTotal = calculateItemsTotal(rejectedItems);

  const renderItemCard = (item) => (
    <div
      key={`${item.id}-${item.variantId}`}
      className="bg-white border border-gray-300 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200"
    >
      <div className="p-3">
        <div className="flex justify-between gap-3">
          {/* Left side: Item details */}
          <div className="flex-1">
            <h4 className="text-base font-medium text-gray-900">
              {item.name}
              {item.isVariant && (
                <span className="text-sm font-normal text-gray-600 ml-1">
                  ({item.variantName})
                </span>
              )}
            </h4>
            <div className="flex items-center gap-1.5 text-sm text-gray-600 mt-0.5">
              <span>₹{Number(item.price).toFixed(2)}</span>
              <span>×</span>
              <span className="bg-gray-100 px-1.5 py-0.5 rounded">
                {item.quantity}
              </span>
            </div>
            {item.itemPreparationNote !== "NA" && (
              <div className="mt-1.5 text-xs text-gray-500">
                <span className="font-medium">Note: </span>
                {item.itemPreparationNote}
              </div>
            )}
          </div>

          {/* Right side: Status and total */}
          <div className="flex flex-col items-end justify-between">
            <span
              className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                item.isCancelled
                  ? "bg-red-100 text-red-700"
                  : item.status === "preparing"
                  ? "bg-yellow-100 text-yellow-700"
                  : item.status === "ready"
                  ? "bg-green-100 text-green-700"
                  : "bg-gray-100 text-gray-700"
              }`}
            >
              {item.isCancelled
                ? "Cancelled"
                : item.status.charAt(0).toUpperCase() + item.status.slice(1)}
            </span>
            <div className="text-base font-semibold text-gray-900">
              ₹{(Number(item.price) * item.quantity).toFixed(2)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const handleSaveOrder = async () => {
    try {
      if (store.orderPaymentMethod === "") {
        toast.error("Select Payment Method");
        return;
      }

      if (
        store.orderPaymentMethod === "other" &&
        store.orderOtherPaymentMethod === ""
      ) {
        toast.error("Please add other Payment Method");
        return;
      }

      store.setLoading(true);

      const methods = [];
      const paymentname = store.orderPaymentMethod.toLowerCase();
      const tableVPA = store.order?.tableVPA;

      if (!tableVPA) {
        toast.error("Invalid table information");
        return;
      }

      if (paymentname === "partial") {
        if (
          !store.partialCardPayment &&
          !store.partialCashPayment &&
          !store.partialOnlinePayment
        ) {
          toast.error("Please enter at least one partial payment amount");
          return;
        }

        const total =
          (parseFloat(store.partialCardPayment) || 0) +
          (parseFloat(store.partialCashPayment) || 0) +
          (parseFloat(store.partialOnlinePayment) || 0);

        if (total !== store.order.billTxnLogs.total) {
          toast.error("Total of partial payments must equal bill amount");
          return;
        }

        methods.push(
          ...[
            {
              name: "card",
              amount: parseFloat(store.partialCardPayment) || 0,
            },
            {
              name: "cash",
              amount: parseFloat(store.partialCashPayment) || 0,
            },
            {
              name: "online",
              amount: parseFloat(store.partialOnlinePayment) || 0,
            },
          ]
        );
      } else if (paymentname === "other") {
        methods.push({
          name: store.orderOtherPaymentMethod.toLowerCase(),
          amount: store.order.billTxnLogs.total,
        });
      } else {
        methods.push({
          name: paymentname,
          amount: store.order.billTxnLogs.total,
        });
      }

      const payment = {
        methods,
        tableVPA,
        paymentname,

        billTxnLogs: {
          ...store.order.billTxnLogs,
          paymentType: paymentname,
        },
        orderId: store.order.orderId,
        isUpdate: !["settle_later", "pending"].includes(store.order.billStatus),
      };

      const response = await completeOrderBill(payment);

      if (response) {
        toast.success("Order updated successfully");
        await onUpdate(store.order.orderId);
      } else {
        toast.error("Please try again !");
      }
    } catch (error) {
      console.error("Error saving order:", error);
      toast.error(error.message || "Failed to save order");
    } finally {
      store.setLoading(false);
    }
  };

  const checkAccess = (credentialResponse) => {
    if (credentialResponse.password === PASSWORD) {
      setShowPasswordDialogue(false);
      setAccessGranted(true);
    } else {
      toast.error("Wrong password");
    }
  };

  if (isOpen && !accessGranted)
    return (
      <PasswordDialog
        isOpen={showPasswordDialogue}
        onClose={() => setShowPasswordDialogue(false)}
        onSubmit={checkAccess}
      />
    );

  return (
    <>
      <div
        className={`fixed inset-y-0 right-0 w-full md:w-[35%] bg-white shadow-lg transform transition-transform duration-300 ease-in-out z-40 ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="sticky top-0 z-10 bg-white p-4 border-b border-gray-300 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold">Order Details</h2>
              {store.order?.billNo && (
                <span className="text-sm text-gray-500">
                  Bill: {store.order.billNo}
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-3 hover:bg-gray-100 rounded-full touch-manipulation"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto flex flex-col">
            {/* Customer Info */}
            <div className="p-4 border-b border-gray-300 bg-gray-50">
              <div className="flex flex-col">
                <div className="text-sm text-gray-600">Customer</div>
                <div className="font-medium">
                  {store.order?.customerName || "NA"}
                </div>
              </div>
            </div>

            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 no-scrollbar">
              {/* Cancelled Items Section */}
              {cancelledItems.length > 0 && (
                <div className="border-t pt-3">
                  <button
                    onClick={() => setShowCancelled(!showCancelled)}
                    className="w-full flex items-center justify-between p-3 bg-red-50 rounded-lg hover:bg-red-100 transition-colors touch-manipulation"
                  >
                    <div className="flex items-center gap-2">
                      <ChevronRight
                        className={`w-5 h-5 transition-transform ${
                          showCancelled ? "rotate-90" : ""
                        }`}
                      />
                      <span className="font-medium text-red-700">
                        Cancelled Items ({cancelledItems.length})
                      </span>
                    </div>
                    <span className="text-sm font-semibold text-red-700">
                      ₹{cancelledTotal.toFixed(2)}
                    </span>
                  </button>
                  {showCancelled && (
                    <div className="mt-3 space-y-3 pl-2">
                      {cancelledItems.map(renderItemCard)}
                    </div>
                  )}
                </div>
              )}

              {/* Rejected Items Section */}
              {rejectedItems.length > 0 && (
                <div className="border-t pt-3">
                  <button
                    onClick={() => setShowRejected(!showRejected)}
                    className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors touch-manipulation"
                  >
                    <div className="flex items-center gap-2">
                      <ChevronRight
                        className={`w-5 h-5 transition-transform ${
                          showRejected ? "rotate-90" : ""
                        }`}
                      />
                      <span className="font-medium text-gray-700">
                        Rejected Items ({rejectedItems.length})
                      </span>
                    </div>
                    <span className="text-sm font-semibold text-gray-700">
                      ₹{rejectedTotal.toFixed(2)}
                    </span>
                  </button>
                  {showRejected && (
                    <div className="mt-3 space-y-3 pl-2">
                      {rejectedItems.map(renderItemCard)}
                    </div>
                  )}
                </div>
              )}
              {/* Active Items */}
              <div className="space-y-3 mb-6">
                {activeItems
                  .slice()
                  .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
                  .map(renderItemCard)}
              </div>
            </div>

            {/* Bill Details */}
            <div className="sticky bottom-0 bg-white border-t border-gray-300">
              <div className="p-4">
                <button
                  className="w-full flex items-center justify-between py-3 hover:bg-gray-50 rounded-md gap-2 touch-manipulation"
                  onClick={() => store.setIsExpanded(!store.isExpanded)}
                >
                  <span className="font-medium flex-1 text-left">
                    Bill Details
                  </span>
                  <span className="text-xl">
                    <b>₹ {store.order?.billTxnLogs.total.toFixed(2)}</b>
                  </span>
                  <ChevronDownIcon
                    className={`w-5 h-5 transition-transform duration-200 ${
                      store.isExpanded ? "" : "rotate-180"
                    }`}
                  />
                </button>

                {store.isExpanded && (
                  <div className="space-y-4 mt-4">
                    {/* Discounts */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      <div>
                        <label className="text-sm text-gray-600 mb-1 block">
                          Flat Discount
                        </label>
                        <div className="flex rounded-md">
                          <input
                            type="text"
                            value={store.flatDiscount}
                            disabled
                            className="w-full rounded-l-md border border-r-0 border-gray-300 px-3 py-2 text-sm focus:outline-none bg-gray-50 text-gray-500 cursor-not-allowed"
                          />
                          <button className="px-3 bg-gray-50 border border-l-0 border-gray-300 rounded-r-md text-sm text-gray-500">
                            Flat
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="text-sm text-gray-600 mb-1 block">
                          Discount(%)
                        </label>
                        <div className="flex rounded-md">
                          <input
                            type="text"
                            value={store.percentageDiscount}
                            disabled
                            className="w-full rounded-l-md border border-r-0 border-gray-300 px-3 py-2 text-sm focus:outline-none bg-gray-50 text-gray-500 cursor-not-allowed"
                          />
                          <button className="px-3 bg-gray-50 border border-l-0 border-gray-300 rounded-r-md text-sm text-gray-500">
                            %
                          </button>
                        </div>
                      </div>
                      <div className="col-span-2 sm:col-span-1">
                        <label className="text-sm text-gray-600 mb-1 block">
                          Tip
                        </label>
                        <input
                          type="number"
                          value={store.tip}
                          disabled
                          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none bg-gray-50 text-gray-500 cursor-not-allowed"
                        />
                      </div>
                    </div>

                    {/* Payment Methods */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {[
                        "Cash",
                        "Card",
                        "Online",
                        "Partial",
                        "Pending",
                        "Other",
                      ].map((method) => {
                        if (
                          method === "Pending" &&
                          !["settle_later", "pending"].includes(
                            store.order?.billStatus
                          )
                        ) {
                          return null;
                        }

                        return (
                          <button
                            key={method}
                            className={`w-full px-3 py-3 text-sm border border-gray-300 rounded-md transition-colors touch-manipulation ${
                              store.orderPaymentMethod === method.toLowerCase()
                                ? "bg-green-500 text-white hover:bg-green-600"
                                : "hover:bg-gray-50"
                            }`}
                            onClick={() =>
                              store.setOrderPaymentMethod(method.toLowerCase())
                            }
                          >
                            {method}
                          </button>
                        );
                      })}
                    </div>

                    {/* Partial Payment */}
                    {store.orderPaymentMethod === "partial" && (
                      <div className="space-y-3 border-t border-gray-300 pt-4">
                        <div className="flex justify-between items-center">
                          <label className="text-sm text-gray-600">Cash</label>
                          <input
                            type="number"
                            value={store.partialCashPayment}
                            onChange={(e) =>
                              handlePartialPaymentChange(e, "Cash")
                            }
                            className="max-w-[140px] rounded-md border border-gray-300 px-3 py-2 text-right text-sm focus:outline-none"
                          />
                        </div>
                        <div className="flex justify-between items-center">
                          <label className="text-sm text-gray-600">
                            Online
                          </label>
                          <input
                            type="number"
                            value={store.partialOnlinePayment}
                            onChange={(e) =>
                              handlePartialPaymentChange(e, "Online")
                            }
                            className="max-w-[140px] rounded-md border border-gray-300 px-3 py-2 text-right text-sm focus:outline-none"
                          />
                        </div>
                        <div className="flex justify-between items-center">
                          <label className="text-sm text-gray-600">Card</label>
                          <input
                            type="number"
                            value={store.partialCardPayment}
                            onChange={(e) =>
                              handlePartialPaymentChange(e, "Card")
                            }
                            className="max-w-[140px] rounded-md border border-gray-300 px-3 py-2 text-right text-sm focus:outline-none"
                          />
                        </div>
                      </div>
                    )}

                    {/* Other Payment Method */}
                    {store.orderPaymentMethod === "other" && (
                      <div className="space-y-3 border-t border-gray-300 pt-4">
                        <div className="flex justify-between items-center">
                          <label className="text-sm text-gray-600">
                            Other method
                          </label>
                          <select
                            value={store.orderOtherPaymentMethod}
                            onChange={(e) =>
                              store.setOrderOtherPaymentMethod(e.target.value)
                            }
                            className="max-w-[200px] rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none"
                          >
                            <option value="zomato dining/district">
                              Zomato Dining/District
                            </option>
                            <option value="swiggy/dineout">
                              ⁠Swiggy/Dineout
                            </option>
                            <option value="magicpin">MagicPin</option>
                            <option value="sodexo">Sodexo</option>
                            <option value="eazydiner">⁠Eazydiner</option>
                            {["settle_later", "pending"].includes(
                              store.order?.billStatus
                            ) && <option value="in-house">In house</option>}
                          </select>
                        </div>
                      </div>
                    )}

                    {/* Amount Breakdown */}
                    <div className="space-y-2 border-t border-gray-300 pt-4">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Subtotal</span>
                        <span>
                          ₹{store.order?.billTxnLogs.subTotal.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">
                          CGST ({store.order?.billTxnLogs.cgstPercent}%)
                        </span>
                        <span>₹{store.order?.billTxnLogs.cgst.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">
                          SGST ({store.order?.billTxnLogs.sgstPercent}%)
                        </span>
                        <span>₹{store.order?.billTxnLogs.sgst.toFixed(2)}</span>
                      </div>
                      {store.order?.billTxnLogs.discount > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Discount</span>
                          <span>
                            ₹{store.order?.billTxnLogs.discount.toFixed(2)}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm font-medium pt-2 border-t border-gray-300">
                        <span>Total</span>
                        <span>
                          ₹{store.order?.billTxnLogs.total.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer Actions */}
              <div className="p-4 border-t border-gray-300">
                <button
                  className="w-full px-4 py-3 text-base font-medium bg-green-500 text-white rounded-md hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors touch-manipulation"
                  onClick={handleSaveOrder}
                  disabled={store.loading}
                >
                  {store.loading ? <Loader /> : "Save Updated Order"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={onClose}
        />
      )}
    </>
  );
});

export default OrderBiller;
